/**
 * Botanical coloring — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_coloring`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const botanicalColoringPack: ToilePack = {
    slug: 'botanical-coloring',
    category: 'coloring',
    name: 'Botanical coloring',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'peony', label: 'Peony', subject: 'one open peony bloom on its stem with two leaves and a tight bud, named "Paeonia officinalis"'},
        {key: 'fern', label: 'Fern frond', subject: 'a fern frond arching from base to tip, each serrated pinna drawn separately, named "Adiantum capillus-veneris"'},
        {key: 'bouquet', label: 'Bouquet', subject: 'eucalyptus sprigs, a monstera leaf and two wild grasses spilling from a stoneware jug, named "Herbarium mixtum"'},
    ],
    styles: [
        {key: 'plate', label: 'Victorian plate', style: 'a Victorian flower-book plate, fine even outlines of uniform thin weight, every vein and edge drawn, no shading', palette: 'black ink only', framing: 'The specimen centered upright with a wide white margin'},
        {key: 'stippled', label: 'Stippled', style: 'an engraved herbarium study, fine outlines with stippled dot shading in the shadowed folds, open enough to color over', palette: 'black ink only', framing: 'The specimen filling most of the page, a narrow white margin at the edges'},
        {key: 'outline', label: 'Clean outline', style: 'a modern single-weight line drawing, one continuous even outline with nothing inside but the main veins', palette: 'black ink only', framing: 'The specimen centered large with a broad white margin'},
    ],
    directions: ['a cactus instead', 'with a butterfly on the stem', 'as a pair of facing pages'],
    seo: {
        title: 'Botanical coloring pages: peony, fern, bouquet',
        metaDescription: 'Nine botanical coloring pages — a peony, a fern frond, a bouquet — drawn as a Victorian plate, stippled or clean outline, ready to print.',
        h1: 'Botanical coloring pages: peony, fern and bouquet in three styles',
        tagline: 'Nine botanical plates, three specimens, three treatments each — the flower book with the color left out.',
        intro: 'Botanical coloring pages recreate the plate from a Victorian flower book: a peony with every petal edge drawn, a fern frond pinna by pinna, a bouquet spilling from a jug — each with its own small name line beneath. Fine even outlines for the classic plate, stippled shading for an engraved herbarium look, or a clean modern outline. Print the set, or ask for a different bloom.',
        useCases: [
            'a botanical wall print series',
            'a nature journal page',
            'a garden club activity',
            'a framed set for a plant lover',
        ],
        faq: [
            {q: 'What size do the pages print at?', a: 'Each page is drawn at portrait A4 proportions (832 by 1216) with a white margin, so it prints clean on standard paper or frames well.'},
            {q: 'Do the pages include a plant name label?', a: "Yes — each specimen carries a small Latin-style name line beneath the drawing, part of the plate's design."},
            {q: 'Can I get a different flower or plant?', a: 'Yes — that is what the direction field on the page is for. Type a different bloom and the plate is drawn again in the same three styles.'},
        ],
    },
}
